import { useMutation, useQuery } from 'react-query';
import { network } from 'services/network';
import {
    TBaselineComparable,
    TDefaultBaseLinePerVersion,
} from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/ProjectPreferences/ProjectPreferences.utils';
import qs from 'qs';

type AcrossVersionHashCodeConfigResponse = { configName: string; isSet: boolean };

type BaseLineDateOption = {
    projectId: number | undefined;
    option?: string;
};

export interface IGetProgressPreferencesResponse {
    name: TBaselineComparable;
    value: 'planned_finish' | 'calculated_finish_date';
    isSelectedOption: boolean;
}

export type AcrossVersionHashCodeConfig = {
    projectId: number;
    isSetActivityId: boolean;
    isSetActivityType: boolean;
    isSetFullPathParentNames: boolean;
    isSetName: boolean;
    isSetParentName: boolean;
    isSetUuid: boolean;
    isSetWbsLevel: boolean;
};

const getBaseLineDateOption = (projectId) => {
    return network
        .get(`api/bo/getBaseLineDateOption?projectId=${projectId}`)
        .then((response) => {
            return response.data;
        })
        .catch((error) => {
            throw error;
        });
};

const getAcrossVersionHashCodeConfig = (projectId) => {
    return network
        .get(`api/bo/getAcrossVersionHashCodeConfig?projectId=${projectId}`)
        .then((response) => {
            return response.data;
        })
        .catch((error) => {
            throw error;
        });
};

const getProgressPreferences = (projectId) => {
    return network
        .post('api/bo/getProgressPreferences', { projectId })
        .then((response) => {
            return response.data;
        })
        .catch((error) => {
            throw error;
        });
};

const setBaseLineDateOption = ({ projectId, option }: BaseLineDateOption) => {
    const querystring = qs.stringify({ projectId, option }, { addQueryPrefix: true, skipNulls: true });
    const url = `api/bo/setBaseLineDateOption${querystring}`;
    return network
        .post(url)
        .then((response) => response.data)
        .catch((error) => {
            throw error;
        });
};

const setProgressPreferences = ({
    projectId,
    selectedOption,
}: {
    projectId: number;
    selectedOption: TBaselineComparable;
}) => {
    return network
        .post('api/bo/setProgressPreferences', { projectId, selectedOption })
        .then((response) => response.data)
        .catch((error) => {
            throw error;
        });
};

const setAcrossVersionHashCodeConfig = ({
    projectId,
    isSetActivityId,
    isSetActivityType,
    isSetFullPathParentNames,
    isSetName,
    isSetParentName,
    isSetUuid,
    isSetWbsLevel,
}: AcrossVersionHashCodeConfig) => {
    const querystring = qs.stringify(
        {
            projectId,
            isSetActivityId,
            isSetActivityType,
            isSetFullPathParentNames,
            isSetName,
            isSetParentName,
            isSetUuid,
            isSetWbsLevel,
        },
        { addQueryPrefix: true, skipNulls: true },
    );
    const url = `api/bo/setAcrossVersionHashCodeConfig${querystring}`;
    return network
        .post(url)
        .then((response) => response.data)
        .catch((error) => {
            throw error;
        });
};

export const useQueryGetBaseLineDateOption = ({ projectId }) => {
    return useQuery<TDefaultBaseLinePerVersion, QueryError>(
        ['useQueryGetBaseLineDateOption', projectId],
        () => getBaseLineDateOption(projectId),
        {
            enabled: Boolean(projectId),
            staleTime: 0,
        },
    );
};

export const useQueryGetAcrossVersionHashCodeConfig = ({ projectId }) => {
    return useQuery<AcrossVersionHashCodeConfigResponse[], QueryError>(
        ['useQueryGetAcrossVersionHashCodeConfig', projectId],
        () => getAcrossVersionHashCodeConfig(projectId),
        {
            enabled: Boolean(projectId),
        },
    );
};

export const useQueryGetProgressPreferences = ({ projectId }) => {
    return useQuery<IGetProgressPreferencesResponse[], QueryError>(
        ['useQueryGetProgressPreferences', projectId],
        () => getProgressPreferences(projectId),
        {
            enabled: Boolean(projectId),
        },
    );
};

export const useMutationSetBaseLineDateOption = () => {
    return useMutation<unknown, QueryError, BaseLineDateOption>((params: BaseLineDateOption) =>
        setBaseLineDateOption(params),
    );
};

export const useMutationSetAcrossVersionHashCodeConfig = () => {
    return useMutation<unknown, QueryError, AcrossVersionHashCodeConfig>((params: AcrossVersionHashCodeConfig) =>
        setAcrossVersionHashCodeConfig(params),
    );
};

export const useMutationSetProgressPreferences = () => {
    return useMutation<
        unknown,
        QueryError,
        {
            projectId: number;
            selectedOption: TBaselineComparable;
        }
    >((params: { projectId: number; selectedOption: TBaselineComparable }) => setProgressPreferences(params));
};
